import { Injectable, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppModeService } from './app-mode-service';
import { environment } from '@/environments/environment';

/**
 * Tipos de mensajes para comunicación iframe <-> host
 */
export type PostMessageType =
  | 'sc:init'
  | 'sc:ready'
  | 'sc:resize'
  | 'sc:requestToken'
  | 'sc:token'
  | 'sc:event'
  | 'sc:navigate';

export interface PostMessageData {
  type: PostMessageType;
  [key: string]: any;
}

export interface InitMessage extends PostMessageData {
  type: 'sc:init';
  user?: string;
  locale?: string;
  theme?: 'light' | 'dark';
  tenant?: string;
  preferredAuth?: 'bearer' | 'cookie';
}

export interface ResizeMessage extends PostMessageData {
  type: 'sc:resize';
  height: number;
}

export interface TokenMessage extends PostMessageData {
  type: 'sc:token';
  accessToken: string;
  refreshToken?: string;
}

export interface EventMessage extends PostMessageData {
  type: 'sc:event';
  name: 'lineAdded' | 'lineDeleted' | 'lineUpdated' | 'error';
  payload: any;
}

@Injectable({ providedIn: 'root' })
export class PostMessageService {
  private parentOrigin = signal<string>('*');
  private accessToken = signal<string | null>(null);
  private config = signal<InitMessage | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _sessionExpired = signal<boolean>(false);
  private _refreshing = false; // semáforo: evita múltiples llamadas simultáneas

  getAccessToken = this.accessToken.asReadonly();
  getConfig = this.config.asReadonly();
  sessionExpired = this._sessionExpired.asReadonly();

  constructor(private appMode: AppModeService) {
    const params = new URLSearchParams(window.location.search);

    // =====================================
    // 1) PREVIEW MODE
    // =====================================
    if (this.appMode.isPreview()) {
      this.loadPreviewConfig(params);
    }

    // =====================================
    // 2) UNIQUE MESSAGE LISTENER
    // =====================================
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter((event) => this.isValidMessage(event)),
        map((event) => event.data as PostMessageData)
      )
      .subscribe((data) => this.handleMessage(data));

    // =====================================
    // 3) REQUEST TOKEN ON BOOT (embed mode)
    // =====================================
    if (this.appMode.isEmbedded() && !this.accessToken()) {
      setTimeout(() => this.requestToken(), 0);
    }
  }

  /** ============================================================
   *  📌 complete config when we are in PREVIEW MODE
   *     (app runing directly on the browser and not a widget)
   *  ============================================================ */
  private loadPreviewConfig(params: URLSearchParams): void {
    const token = params.get('token');
    const tenant = params.get('tenant') ?? 'CO'; // 👈
    const locale = params.get('locale') ?? 'es-CO';
    const theme = params.get('theme') ?? ('light' as any);

    // 🔐 TOKEN handling
    if (token) {
      !environment.production && console.log('[Preview] Token por query param:', token);
      this.accessToken.set(token);
      localStorage.setItem('sc-token-dev', token);
      this.scheduleTokenRefresh(token);
    } else {
      const local = localStorage.getItem('sc-token-dev');
      if (local) {
        !environment.production &&
          console.log('[Preview] Usando token previamente guardado:', local);
        this.accessToken.set(local);
        this.scheduleTokenRefresh(local);
      }
    }

    // 🧩 CONFIG handling
    const config: InitMessage = {
      type: 'sc:init',
      tenant,
      locale,
      theme,
      preferredAuth: 'bearer',
    };

    this.config.set(config);

    !environment.production && console.log('[Preview] Config generada:', config);
  }

  private isValidMessage(event: MessageEvent): boolean {
    return event.data && typeof event.data === 'object' && event.data.type;
  }

  private isInitMessage(data: PostMessageData): data is InitMessage {
    return data.type === 'sc:init';
  }

  private isTokenMessage(data: PostMessageData): data is TokenMessage {
    return data.type === 'sc:token';
  }

  private handleMessage(data: PostMessageData): void {
    switch (data.type) {
      case 'sc:init':
        if (this.isInitMessage(data)) {
          this.config.set(data);
        }
        break;

      case 'sc:token':
        if (this.isTokenMessage(data)) {
          if (this.appMode.isEmbedded()) {
            !environment.production &&
              console.log('[PostMessage] 🔑 Token recibido (embebido):', data.accessToken);
          }

          this.accessToken.set(data.accessToken);
          this._sessionExpired.set(false);
          this._refreshing = false;
          this.scheduleTokenRefresh(data.accessToken);
        }
        break;

      case 'sc:ready':
      case 'sc:resize':
      case 'sc:requestToken':
      case 'sc:event':
      case 'sc:navigate':
        // Mensajes salientes que rebotan en WebView nativo (window.parent === window). Ignorar.
        break;

      default:
        console.warn('[PostMessage] Tipo de mensaje desconocido:', data.type);
    }
  }

  // ============================================================
  // TOKEN REFRESH — Opción 3 (timer proactivo) + Opción 2 (bridge nativo)
  // ============================================================

  private isTokenValid(token: string): boolean {
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      return exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      const delay = exp * 1000 - Date.now() - 60_000; // 60s antes de expirar
      if (delay > 0) {
        !environment.production &&
          console.log(`[TokenRefresh] Refresh programado en ${Math.round(delay / 1000)}s`);
        this.refreshTimer = setTimeout(() => this.requestTokenRefresh(), delay);
      } else {
        // Ya expiró o está a punto de hacerlo
        this.requestTokenRefresh();
      }
    } catch {
      !environment.production && console.warn('[TokenRefresh] No se pudo parsear el token JWT');
    }
  }

  /**
   * Solicita un token nuevo al entorno padre.
   * Prioridad: bridge Android → bridge iOS → postMessage → reload (fallback)
   *
   * Llamado automáticamente por el timer o desde el interceptor HTTP (401).
   * Cuando la app nativa tenga el bridge listo, responderá invocando window.injectToken(newToken).
   */
  requestTokenRefresh(): void {
    if (this._refreshing) return; // ya hay un refresh en progreso
    this._refreshing = true;
    !environment.production && console.log('[TokenRefresh] Solicitando refresh de token...');

    // 1. Bridge Android (addJavascriptInterface)
    if ((window as any).Android?.requestTokenRefresh) {
      (window as any).Android.requestTokenRefresh();
      return;
    }

    // 2. Bridge iOS (WKScriptMessageHandler)
    if ((window as any).webkit?.messageHandlers?.nativeBridge) {
      (window as any).webkit.messageHandlers.nativeBridge.postMessage({
        type: 'requestTokenRefresh',
      });
      return;
    }

    // 3. iframe postMessage (sc:requestToken → el padre responde con sc:token)
    this.requestToken();

    // 4. Sin bridge nativo ni iframe padre → sesión expirada
    //    Cuando se integre el bridge nativo (Android/iOS), este punto no se alcanzará.
    !environment.production &&
      console.warn('[TokenRefresh] Sin bridge disponible. Mostrando pantalla de sesión expirada.');
    this._sessionExpired.set(true);
  }

  /**
   * Inyecta un token nuevo recibido desde la app nativa.
   * La app nativa debe llamar: window.injectToken("nuevo_jwt")
   */
  injectToken(token: string): void {
    !environment.production && console.log('[TokenRefresh] Token inyectado por la app nativa');
    this._sessionExpired.set(false);
    this._refreshing = false;
    this.accessToken.set(token);
    localStorage.setItem('sc-token-dev', token);
    sessionStorage.removeItem('sc-reload-attempt');
    // Actualizar URL sin recargar para que un futuro reload tenga el token correcto
    const url = new URL(window.location.href);
    url.searchParams.set('token', token);
    window.history.replaceState({}, '', url.toString());
    this.scheduleTokenRefresh(token);
  }

  private sendToParent(message: PostMessageData): void {
    if (!this.appMode.isEmbedded()) {
      return;
    }

    window.parent.postMessage(message, this.parentOrigin());
  }

  notifyReady(): void {
    this.sendToParent({ type: 'sc:ready' });
  }

  notifyResize(height: number): void {
    this.sendToParent({ type: 'sc:resize', height });
  }

  requestToken(): void {
    this.sendToParent({ type: 'sc:requestToken' });
  }

  emitEvent(name: string, payload: any): void {
    this.sendToParent({ type: 'sc:event', name, payload });
  }

  requestNavigation(path: string): void {
    this.sendToParent({ type: 'sc:navigate', path });
  }

  startAutoResize(): void {
    if (!this.appMode.isEmbedded()) return;

    const observer = new ResizeObserver(() => {
      const height = document.body.scrollHeight;
      this.notifyResize(height);
    });

    observer.observe(document.body);
  }
}
